import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Target, Ticket, Mail, Users, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function FundraisingPage() {
  const { toast } = useToast();
  const { ptaId } = useParams();
  const [parentCode, setParentCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [ptaData, setPtaData] = useState<any>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);

  useEffect(() => {
    fetchPTAData();
  }, [ptaId]);

  const fetchPTAData = async () => {
    setLoading(true);
    
    // Fetch PTA data with subscription info
    const { data: pta, error } = await supabase
      .from('ptas')
      .select(`
        *,
        subscriptions (plan, status)
      `)
      .eq('id', ptaId || '')
      .single();

    if (error || !pta) {
      // Show generic/demo page if PTA not found or no ID provided
      setHasAccess(false);
      setPtaData(null);
    } else {
      setPtaData(pta);
      // Check if PTA has standard plan
      const subscription = pta.subscriptions?.[0];
      setHasAccess(subscription?.plan === 'standard' && subscription?.status === 'active');
      
      // Fetch sponsorship data
      if (pta.id) {
        const { data: sponsorship } = await supabase
          .from('sponsorships')
          .select('*')
          .eq('pta_id', pta.id)
          .eq('status', 'active')
          .eq('show_on_fundraising', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        setSponsorshipData(sponsorship);
      }
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Plan upgrade prompt
  if (!hasAccess && ptaData) {
    return (
      <div className="min-h-screen bg-gradient-hero">
        <div className="container py-16">
          <Card className="max-w-2xl mx-auto shadow-elevated">
            <CardHeader className="text-center">
              <div className="mx-auto w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center mb-4">
                <Lock className="h-10 w-10 text-primary-foreground" />
              </div>
              <CardTitle className="text-3xl">Upgrade to Access Fundraising Page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <p className="text-center text-muted-foreground">
                  The Fundraising Page feature is available on our Standard plan
                </p>
                
                <Alert>
                  <AlertDescription>
                    <h4 className="font-semibold mb-2">Standard Plan Features:</h4>
                    <ul className="space-y-1 text-sm ml-4">
                      <li>✅ Beautiful public fundraising page</li>
                      <li>✅ Customizable header with your logo and colors</li>
                      <li>✅ Display events and fundraising goals</li>
                      <li>✅ Accept donations and ticket sales</li>
                      <li>✅ Parent portal access</li>
                      <li>✅ Team member profiles</li>
                      <li>✅ Contact form integration</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <div className="pt-4">
                  <Button className="w-full" size="lg">
                    Upgrade to Standard Plan
                  </Button>
                  <p className="text-center text-sm text-muted-foreground mt-2">
                    Or add Fundraising Page as an addon to your Basic plan
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const headerColor = ptaData?.header_color || '#6366f1';
  const headerTemplate = ptaData?.header_template || 'left';
  const logoUrl = ptaData?.logo_url;
  const showMembers = ptaData?.show_pta_members !== false;
  const showContactDetails = ptaData?.show_contact_details !== false;

  const events = [
    { name: "Summer Fair", date: "June 15, 2025", description: "Join us for games, food, and family fun!" },
    { name: "Bake Sale", date: "May 10, 2025", description: "Delicious homemade treats" },
  ];

  const goals = [
    { name: "Playground Equipment", target: "£5,000", raised: "£3,000", progress: 60 },
    { name: "Library Books", target: "£1,500", raised: "£450", progress: 30 },
  ];

  const ptaMembers = [
    { name: "Sarah Smith", role: "Chair", bio: "Passionate about creating opportunities for our children" },
    { name: "John Doe", role: "Treasurer", bio: "Ensuring transparency in all our finances" },
    { name: "Emma Wilson", role: "Secretary", bio: "Keeping everyone organized and informed" },
  ];

  return (
    <main className="min-h-screen bg-gradient-hero">
      {/* Customizable Header */}
      <div 
        className="border-b border-border/50 shadow-sm"
        style={{ backgroundColor: headerColor }}
      >
        <div className="container py-8">
          <div 
            className={`flex items-center gap-4 ${
              headerTemplate === 'center' ? 'justify-center text-center' : 
              headerTemplate === 'right' ? 'justify-end' : 
              'justify-start'
            }`}
          >
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="PTA Logo" 
                className="w-16 h-16 rounded-lg object-cover shadow-card"
              />
            )}
            {!logoUrl && (
              <div className="w-16 h-16 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <span className="text-3xl">🏫</span>
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white drop-shadow-lg">
                {ptaData?.name || 'PTA Fundraising'}
              </h1>
              <p className="text-white/90 drop-shadow">
                {ptaData?.school_name || 'Supporting our school community'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8 space-y-8">
        {sponsorshipData && (
          <Card className="shadow-card border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <CardContent className="pt-6">
              <p className="text-center text-lg">
                <span className="text-muted-foreground">PTA Sponsored by:</span>{' '}
                <span className="font-bold text-primary">{sponsorshipData.business_name}</span>
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="events" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="goals">Goals</TabsTrigger>
            <TabsTrigger value="donate">Donate</TabsTrigger>
            <TabsTrigger value="tickets">Tickets</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="space-y-4 animate-fade-in">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Upcoming Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {events.map((event, i) => (
                    <div key={i} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow">
                      <div className="flex items-start gap-3">
                        <Calendar className="h-6 w-6 text-primary mt-1 shrink-0" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{event.name}</h3>
                          <p className="text-sm text-primary font-medium mb-2">{event.date}</p>
                          <p className="text-sm text-muted-foreground">{event.description}</p>
                          <Button className="mt-3" size="sm">Learn More</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="goals" className="space-y-4 animate-fade-in">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Fundraising Goals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {goals.map((goal, i) => (
                    <div key={i} className="space-y-3 p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Target className="h-4 w-4 text-primary" />
                          {goal.name}
                        </h3>
                        <span className="text-sm font-medium text-primary">
                          {goal.raised} / {goal.target}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="h-3 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-500 ease-out" 
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground text-right">{goal.progress}% funded</p>
                      </div>
                      <Button size="sm" className="w-full">Support This Goal</Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="donate" className="space-y-4 animate-fade-in">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Make a Donation</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-4">
                  <div className="grid grid-cols-4 gap-2">
                    {['£5', '£10', '£25', '£50'].map((amount) => (
                      <Button key={amount} type="button" variant="outline" className="h-12">
                        {amount}
                      </Button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donate-amount">Custom Amount (£)</Label>
                    <Input id="donate-amount" type="number" placeholder="10.00" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donate-name">Your Name</Label>
                    <Input id="donate-name" placeholder="John Smith" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donate-email">Email Address</Label>
                    <Input id="donate-email" type="email" placeholder="you@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donate-message">Message (Optional)</Label>
                    <Textarea id="donate-message" placeholder="Add a message of support..." rows={3} />
                  </div>
                  <Button type="submit" className="w-full" size="lg">Complete Donation</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tickets" className="space-y-4 animate-fade-in">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-primary" />
                  Event Tickets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-6 border-2 border-primary/20 rounded-lg bg-gradient-to-br from-primary/5 to-transparent">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Ticket className="h-5 w-5 text-primary" />
                          Summer Fair Entry
                        </h3>
                        <p className="text-sm text-muted-foreground">June 15, 2025</p>
                      </div>
                      <span className="text-2xl font-bold text-primary">£5.00</span>
                    </div>
                    <p className="text-sm mb-4">Includes entry for one person plus event program</p>
                    <Button className="w-full" size="lg">Purchase Ticket</Button>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-3">Available Extras</h4>
                    <div className="space-y-2">
                      {[
                        { name: 'Hot Dog', price: '£2.50' },
                        { name: 'Drink', price: '£1.00' },
                        { name: 'Raffle Ticket', price: '£1.00' }
                      ].map((extra, i) => (
                        <div key={i} className="flex items-center justify-between p-2 hover:bg-background rounded transition-colors">
                          <span className="text-sm">{extra.name}</span>
                          <span className="text-sm font-medium">{extra.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Parent Access Section */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Parent Access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Already registered? Enter your three-word parent code to access your account
            </p>
            <div className="flex gap-2">
              <Input 
                placeholder="example-word-code"
                value={parentCode}
                onChange={(e) => setParentCode(e.target.value)}
              />
              <Button onClick={() => {
                toast({
                  title: "Access granted",
                  description: "Welcome back to your PTA account",
                });
              }}>
                Access Account
              </Button>
            </div>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.location.href = '/portal'}
            >
              New Parent? Register Here
            </Button>
          </CardContent>
        </Card>

        {showContactDetails && (
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Name</Label>
                    <Input id="contact-name" placeholder="Your name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input id="contact-email" type="email" placeholder="your@email.com" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea id="contact-message" placeholder="How can we help?" rows={4} />
                </div>
                <Button type="submit" className="w-full">Send Message</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* PTA Info and Members */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>About Our PTA</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Our PTA is dedicated to supporting {ptaData?.school_name || 'our school'} and creating 
                  amazing opportunities for our children. We organize events, raise funds, and 
                  build a strong community.
                </p>
                {showContactDetails && (
                  <div className="pt-3 border-t border-border space-y-1">
                    {ptaData?.charity_number && (
                      <p className="text-sm"><strong>Charity Number:</strong> {ptaData.charity_number}</p>
                    )}
                    <p className="text-sm"><strong>School:</strong> {ptaData?.school_name || 'N/A'}</p>
                    {ptaData?.shared_email && (
                      <p className="text-sm"><strong>Email:</strong> {ptaData.shared_email}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {showMembers && (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Meet the Team
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ptaMembers.map((member, i) => (
                    <div key={i} className="p-3 border border-border rounded-lg hover:shadow-sm transition-shadow">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground font-semibold shrink-0">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <h4 className="font-semibold">{member.name}</h4>
                          <p className="text-sm text-primary">{member.role}</p>
                          <p className="text-xs text-muted-foreground mt-1">{member.bio}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground pt-2">
                    Team members can opt-out of public profile display
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </main>
  );
}
